import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { recruiterApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { FileText, AlertCircle, Calendar, Copy, ExternalLink, Trash2, Loader2 } from 'lucide-react'

interface Form {
  id: number
  uuid: string
  job_description: string | null
  questions: Array<any>
  created_at: string
  updated_at: string
}

export default function FormsPage() {
  const { recruiter } = useAuth()
  const navigate = useNavigate()
  const [forms, setForms] = useState<Form[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedUuid, setCopiedUuid] = useState<string | null>(null)
  const [formToDelete, setFormToDelete] = useState<Form | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null)

  const fetchForms = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await recruiterApi.getForms()
      
      if (response.status === 'success') {
        setForms(response.forms)
      } else {
        setError('שגיאה בטעינת הטפסים')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בטעינת הטפסים')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchForms()
  }, [recruiter])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return '—'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const handleCopyUuid = async (uuid: string) => {
    try {
      await navigator.clipboard.writeText(uuid)
      setCopiedUuid(uuid)
      setTimeout(() => setCopiedUuid(null), 2000)
    } catch (err) {
      console.error('Failed to copy UUID:', err)
    }
  }

  const handleViewForm = (uuid: string) => {
    navigate(`/forms/${uuid}/overview`)
  }

  const handleDeleteClick = (form: Form) => {
    setFormToDelete(form)
  }

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return

    try {
      setIsDeleting(true)
      setError(null)
      await recruiterApi.deleteForm(formToDelete.id)
      setFormToDelete(null)
      // Refresh the forms list
      await fetchForms()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה במחיקת הטופס')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setFormToDelete(null)
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="bg-background p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">טפסים</h1>
          <p className="text-muted-foreground mt-1">
            רשימת כל הטפסים שנוצרו במערכת
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{forms.length} טפסים</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Forms Table */}
      {forms.length === 0 && !error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">
                אין טפסים במערכת
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                צור טפסים חדשים בעמוד בונה הערכת מועמדים
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>רשימת טפסים</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-right p-4 font-semibold">תיאור משרה</th>
                    <th className="text-right p-4 font-semibold">מספר שאלות</th>
                    <th className="text-right p-4 font-semibold">תאריך יצירה</th>
                    <th className="text-right p-4 font-semibold">מזהה טופס</th>
                    <th className="text-right p-4 font-semibold w-32">פעולות</th>
                    <th className="text-right p-4 font-semibold w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((form) => (
                    <tr 
                      key={form.id} 
                      className="border-b hover:bg-muted/50 transition-colors"
                      onMouseEnter={() => setHoveredRowId(form.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <td className="p-4">
                        <div className="max-w-md">
                          <p className="text-sm text-gray-900">
                            {truncateText(form.job_description)}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="default">
                          {form.questions?.length || 0} שאלות
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{formatDate(form.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {form.uuid.substring(0, 8)}...
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleCopyUuid(form.uuid)}
                            title="העתק מזהה טופס"
                          >
                            <Copy className={`h-3 w-3 ${copiedUuid === form.uuid ? 'text-green-600' : ''}`} />
                          </Button>
                        </div>
                      </td>
                      <td className="p-4 w-32">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewForm(form.uuid)}
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          צפה
                        </Button>
                      </td>
                      <td className="p-4 w-16">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 transition-opacity duration-200 ${
                            hoveredRowId === form.id
                              ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                              : 'opacity-0 invisible'
                          }`}
                          onClick={() => handleDeleteClick(form)}
                          title="מחק טופס"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {formToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>מחיקת טופס</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                האם אתה בטוח שברצונך למחוק את הטופס?
              </p>
              {formToDelete.job_description && (
                <p className="text-xs text-muted-foreground">
                  <strong>תיאור משרה:</strong> {truncateText(formToDelete.job_description, 50)}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                פעולה זו לא ניתנת לביטול.
              </p>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                >
                  ביטול
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      מוחק...
                    </>
                  ) : (
                    'מחק'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
